import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../../components/page-layout";
import { PermissionGate } from "../../components/PermissionGate";
import { usePermission } from "../../providers/PermissionsProvider";
import {
  List,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import {
  PageHeader,
  Panel,
  SectionHeader,
  StatGrid,
  Stat,
  Tag,
  OnyxPrimaryButton,
  OnyxSecondaryButton,
} from "../../components/onyx";

export const AdminDashboard = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { can } = usePermission();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    pendingApprovals: 0,
    organizations: 0,
    plans: 0
  });
  const [loading, setLoading] = useState(false);

  const hasProvisioning = can('admin:provisioning');
  const hasSurveyManagement = can('admin:survey_management');

  useEffect(() => {
    loadAdminStats();
  }, [hasProvisioning, hasSurveyManagement]);

  const loadAdminStats = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const headers = { 'Authorization': `Bearer ${token}` };

      const userListResponse = await fetch(
        `${process.env.REACT_APP_API_SERVER_URL}/api/admin/users/?page=1&per_page=1`,
        { headers }
      );
      if (userListResponse.ok) {
        const data = await userListResponse.json();
        setStats(prev => ({ ...prev, totalUsers: data.total || 0 }));
      }

      if (hasSurveyManagement) {
        const approvalsResponse = await fetch(
          `${process.env.REACT_APP_API_SERVER_URL}/api/admin/pending-approvals/`,
          { headers }
        );
        if (approvalsResponse.ok) {
          const data = await approvalsResponse.json();
          const pending = data.pending || data.users || [];
          setPendingApprovals(pending);
          setStats(prev => ({ ...prev, pendingApprovals: pending.length }));
        }
      }

      if (hasProvisioning) {
        const orgsResponse = await fetch(
          `${process.env.REACT_APP_API_SERVER_URL}/api/admin/provisioning/organizations/`,
          { headers }
        );
        if (orgsResponse.ok) {
          const data = await orgsResponse.json();
          setStats(prev => ({ ...prev, organizations: data.count || 0 }));
        }

        const plansResponse = await fetch(
          `${process.env.REACT_APP_API_SERVER_URL}/api/admin/provisioning/plans/`,
          { headers }
        );
        if (plansResponse.ok) {
          const data = await plansResponse.json();
          setStats(prev => ({ ...prev, plans: data.count || 0 }));
        }
      }
    } catch (err) {
      console.error('Failed to load admin stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const showQuickActions = can('org_user:manage') || hasProvisioning || can('admin:security');

  return (
    <PageLayout>
      <div className="content-layout">
        <PageHeader
          title="Dashboard"
          description="Overview of your assigned users and tasks."
        />

        {showQuickActions ? (
          <Panel aria-label="Quick actions">
            <SectionHeader title="Quick Actions" />
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <PermissionGate require="org_user:manage">
                <OnyxPrimaryButton
                  chevron={false}
                  onClick={() => navigate('/admin/invite')}
                >
                  Invite Member
                </OnyxPrimaryButton>
              </PermissionGate>

              <PermissionGate require="admin:provisioning">
                <OnyxSecondaryButton
                  onClick={() => navigate('/admin/security')}
                >
                  Add Organization
                </OnyxSecondaryButton>
              </PermissionGate>

              <PermissionGate require="admin:security">
                <OnyxSecondaryButton
                  onClick={() => navigate('/admin/security')}
                >
                  Manage Security
                </OnyxSecondaryButton>
              </PermissionGate>
            </Stack>
          </Panel>
        ) : null}

        <StatGrid
          layout="row"
          aria-busy={loading}
          data-testid="admin-dashboard-stats"
        >
          <Stat
            as="button"
            type="button"
            density="compact"
            value={stats.totalUsers}
            label="Total Users"
            onClick={() => navigate('/admin/users')}
          />

          {hasSurveyManagement ? (
            <Stat
              as="button"
              type="button"
              density="compact"
              value={stats.pendingApprovals}
              label="Pending Approvals"
              onClick={() => navigate('/admin/survey-management')}
            />
          ) : null}

          {hasProvisioning ? (
            <Stat
              density="compact"
              value={stats.organizations}
              label="Organizations"
            />
          ) : null}

          {hasProvisioning ? (
            <Stat
              density="compact"
              value={stats.plans}
              label="Plans"
            />
          ) : null}
        </StatGrid>

        {pendingApprovals.length > 0 ? (
          <Panel aria-label="Pending approvals">
            <SectionHeader
              title={(
                <>
                  <PendingActionsIcon fontSize="inherit" />
                  {' '}
                  Pending Approvals
                </>
              )}
              actions={(
                <OnyxSecondaryButton
                  size="small"
                  onClick={() => navigate('/admin/users')}
                >
                  View All
                </OnyxSecondaryButton>
              )}
            />
            <List disablePadding>
              {pendingApprovals.slice(0, 5).map((user) => (
                <ListItem
                  key={user.id}
                  button
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                >
                  <ListItemText
                    primary={user.name?.first ? `${user.name.first} ${user.name.last}` : user.name}
                    secondary={user.email}
                  />
                  <Tag variant="warning">Pending</Tag>
                </ListItem>
              ))}
            </List>
          </Panel>
        ) : null}

        <Panel aria-label="Available tools">
          <SectionHeader title="Available Tools" />
          <ul className="onyx-text onyx-text--body-m">
            <li>
              <strong>User Management</strong>
              {' — '}
              View user profiles, onboarding status, and clinical data
            </li>
            <PermissionGate require="admin:survey_management">
              <li>
                <strong>Survey Management</strong>
                {' — '}
                Track participants and manage CRC assignments
              </li>
            </PermissionGate>
            <PermissionGate require="org_user:manage">
              <li>
                <strong>Invitations</strong>
                {' — '}
                Invite new members to the platform
              </li>
            </PermissionGate>
            <PermissionGate require="admin:provisioning">
              <li>
                <strong>Provisioning</strong>
                {' — '}
                Create organizations, assign plans, configure entitlements
              </li>
            </PermissionGate>
            <PermissionGate require="admin:security">
              <li>
                <strong>Security</strong>
                {' — '}
                Manage roles, permissions, and access control
              </li>
            </PermissionGate>
            <PermissionGate require="admin:member_funnel">
              <li>
                <strong>Member Funnel</strong>
                {' — '}
                Track member enrollment and sample collections
              </li>
            </PermissionGate>
            <PermissionGate require="admin:workflows">
              <li>
                <strong>Data Tools</strong>
                {' — '}
                Cohort analysis, LLM jobs, data uploads
              </li>
            </PermissionGate>
          </ul>
        </Panel>
      </div>
    </PageLayout>
  );
};
